import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/** Keyboard hint chip. */
export function Kbd({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <kbd
      className={cn(
        "inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded border border-border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground",
        className
      )}
    >
      {children}
    </kbd>
  );
}

/** Page header with title, description and optional actions. */
export function PageHeader({
  title,
  description,
  actions,
  icon,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex items-start gap-3">
        {icon}
        <div className="space-y-1">
          {/* Field Suite page-title scale: big, bold, confident. */}
          <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{title}</h2>
          {description && (
            <p className="max-w-2xl text-[0.95rem] text-muted-foreground">{description}</p>
          )}
        </div>
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

export function SectionLabel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <h3
      className={cn(
        "text-xs font-semibold uppercase tracking-wider text-muted-foreground",
        className
      )}
    >
      {children}
    </h3>
  );
}

/** A benchmark bar: current value vs a target, color-staged. */
export function BenchmarkBar({
  value,
  max = 100,
  benchmark,
  good = "high",
  unit = "%",
}: {
  value: number | null;
  max?: number;
  benchmark?: number;
  good?: "high" | "low";
  unit?: string;
}) {
  const v = value ?? 0;
  const ratio = Math.max(0, Math.min(1, v / max));
  const hitsBenchmark =
    benchmark == null ? true : good === "high" ? v >= benchmark : v <= benchmark;
  const barColor = hitsBenchmark ? "bg-primary" : "bg-cta";
  return (
    <div className="space-y-1">
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn("h-full rounded-full transition-all duration-700", barColor)}
          style={{ width: `${ratio * 100}%` }}
        />
        {benchmark != null && (
          <div
            className="absolute top-0 h-full w-0.5 bg-foreground/50"
            style={{ left: `${Math.min(100, (benchmark / max) * 100)}%` }}
            title={`Benchmark: ${benchmark}${unit}`}
          />
        )}
      </div>
      {benchmark != null && (
        <p className="text-[11px] text-muted-foreground">
          Target {good === "high" ? "≥" : "≤"} {benchmark}
          {unit}
          {value != null && (
            <span className={cn("ml-1 font-medium", hitsBenchmark ? "text-primary" : "text-cta")}>
              · {hitsBenchmark ? "on track" : "below"}
            </span>
          )}
        </p>
      )}
    </div>
  );
}

/** Circular progress ring. */
export function ProgressRing({
  value,
  size = 120,
  stroke = 10,
  children,
  tone = "primary",
}: {
  value: number; // 0..100
  size?: number;
  stroke?: number;
  children?: ReactNode;
  tone?: "primary" | "cta";
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.max(0, Math.min(100, value)) / 100) * c;
  const color = tone === "cta" ? "var(--cta)" : "var(--primary)";
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--muted)" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 0.8s cubic-bezier(0.22,1,0.36,1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        {children}
      </div>
    </div>
  );
}

/** Empty state that always points at the next action. */
export function EmptyState({
  icon,
  title,
  children,
  action,
}: {
  icon?: ReactNode;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border bg-card/40 px-6 py-12 text-center">
      {icon && <div className="text-muted-foreground">{icon}</div>}
      <div className="space-y-1">
        <p className="font-medium">{title}</p>
        {children && (
          <p className="mx-auto max-w-sm text-sm text-muted-foreground">{children}</p>
        )}
      </div>
      {action}
    </div>
  );
}
